import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import Cookies from 'js-cookie';
import {
	MdApartment,
	MdApps,
	MdArrowBack,
	MdArrowOutward,
	MdClose,
	MdInsights,
	MdPublic,
	MdSearch,
} from 'react-icons/md';
import NavBar from '../../../components/NavBar';

import SearchableSetorSelect from '../../../components/Escalas Utils/SearchableSetorSelect';
import { requestDashboard } from '../../../services/requests';
import './PaineisMonitoramento.css';

const CATEGORY_COLORS = [
	'#6693cb',
	'#8abcc0',
	'#a9a9db',
	'#89bd9f',
	'#d89e94',
	'#c3a0cb',
	'#b9b082',
	'#caa882',
	'#88b8d1',
	'#d49cb1',
];

const normalize = (value = '') =>
	String(value || '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.trim();

const getUnitName = (unit = {}) =>
	String(
		unit?.unidade_nome ||
			unit?.nome_unidade ||
			unit?.nome ||
			unit?.unidade ||
			'',
	).trim();

const getUnitId = (unit = {}) =>
	String(unit?.unidade_id || unit?.id || unit?.pk || unit?.cnes || '').trim();

const asArray = (value) =>
	Array.isArray(value) ? value : [value].filter(Boolean);

const UNIT_FAMILY_ORDER = [
	'hospitais',
	'upa',
	'cer',
	'maternidades',
	'centros',
	'outras',
];

const getUnitFamily = (unitName = '') => {
	const name = normalize(unitName);
	if (name.startsWith('upa '))
		return { id: 'upa', name: 'UPA', color: '#bd504e' };
	if (name.startsWith('cer '))
		return { id: 'cer', name: 'CER', color: '#009a99' };
	if (/(maternidade|casa de parto|hospital da mulher)/.test(name)) {
		return {
			id: 'maternidades',
			name: 'Maternidades e Casa de Parto',
			color: '#9a5bb1',
		};
	}
	if (name.startsWith('centro') || name.startsWith('instituto')) {
		return {
			id: 'centros',
			name: 'Centros e Institutos',
			color: '#4b8d3c',
		};
	}
	if (name.startsWith('hospital'))
		return {
			id: 'hospitais',
			name: 'Hospitais gerais e especializados',
			color: '#3777bb',
		};
	return { id: 'outras', name: 'Outras unidades', color: '#71829a' };
};

const groupPanelsByUnit = (items = []) => {
	const panels = new Map();

	items.forEach((dashboard) => {
		const category = String(dashboard?.categoria_nome || 'Sem categoria');
		const name = String(dashboard?.nome || 'Painel sem nome');
		const isSubhue = dashboard?.subhue === true;
		const key = `${normalize(category)}:${normalize(name)}`;

		if (!panels.has(key)) {
			panels.set(key, {
				id: key,
				categoria_nome: category,
				nome: name,
				icone: dashboard?.icone,
				sourceDashboard: dashboard,
				centralDashboard: null,
				unitsByKey: new Map(),
			});
		}

		const panel = panels.get(key);
		if (!panel.icone && dashboard?.icone) panel.icone = dashboard.icone;
		if (isSubhue) {
			panel.centralDashboard = dashboard;
			return;
		}

		(dashboard?.unidades || []).forEach((unit) => {
			const unitName = getUnitName(unit);
			if (!unitName) return;
			const unitKey = `${normalize(unitName)}:${getUnitId(unit)}`;
			if (!panel.unitsByKey.has(unitKey))
				panel.unitsByKey.set(unitKey, {
					...unit,
					name: unitName,
					dashboard,
				});
		});
	});

	return Array.from(panels.values()).map(({ unitsByKey, ...panel }) => ({
		...panel,
		unidades: Array.from(unitsByKey.values()).sort((a, b) =>
			a.name.localeCompare(b.name, 'pt-BR'),
		),
	}));
};

const getUser = () => {
	try {
		return Cookies.get('user') ? JSON.parse(Cookies.get('user')) : null;
	} catch {
		return null;
	}
};

const canAccessAllUnits = (user = {}) => {
	const cargos = asArray(user?.cargo).map(normalize);
	const unidades = asArray(user?.unidade).map(normalize);
	return (
		user?.smsrio === true ||
		user?.is_subhue === true ||
		cargos.includes('desenvolvedor') ||
		cargos.includes('sms') ||
		unidades.some((unit) => ['subhue', 'sms'].includes(unit))
	);
};

const getAccessibleProfileUnits = (user = {}) => {
	const profileUnits = asArray(user?.perfil_unidades).filter(
		(unit) => unit?.ativo !== false,
	);
	if (profileUnits.length) return profileUnits;

	const fallbackName = asArray(user?.unidade).find(
		(unit) => !['subhue', 'sms'].includes(normalize(unit)),
	);
	return fallbackName || user?.id
		? [
				{
					nome_unidade: fallbackName,
					unidade_id: user?.id,
				},
			]
		: [];
};

const isAllowedUnit = (unit, accessibleUnits) => {
	const unitName = normalize(getUnitName(unit));
	const unitId = getUnitId(unit);
	return accessibleUnits.some((accessibleUnit) => {
		const accessibleName = normalize(getUnitName(accessibleUnit));
		const accessibleId = getUnitId(accessibleUnit);
		return (
			(unitId && accessibleId && unitId === accessibleId) ||
			(unitName && accessibleName && unitName === accessibleName)
		);
	});
};

const PaineisMonitoramento = () => {
	const user = useMemo(getUser, []);
	const token = user?.token;
	const hasFullUnitAccess = useMemo(() => canAccessAllUnits(user), [user]);
	const accessibleProfileUnits = useMemo(
		() => getAccessibleProfileUnits(user),
		[user],
	);
	const [dashboards, setDashboards] = useState([]);
	const [loading, setLoading] = useState(true);
	const [selectedCategory, setSelectedCategory] = useState('all');
	const [selectedUnit, setSelectedUnit] = useState('all');
	const [search, setSearch] = useState('');
	const [selectedDashboard, setSelectedDashboard] = useState(null);
	const [drawerDashboard, setDrawerDashboard] = useState(null);
	const [drawerSearch, setDrawerSearch] = useState('');
	const drawerSearchRef = useRef(null);

	const fetchDashboards = useCallback(async () => {
		if (!token) {
			setLoading(false);
			return;
		}
		const unitToken = user?.id
			? String(user.id).replace(/-/g, '').split('').reverse().join('')
			: '';
		setLoading(true);
		try {
			const response = await requestDashboard({ token, unitToken });
			setDashboards(
				Array.isArray(response)
					? response
					: Array.isArray(response?.data)
						? response.data
						: [],
			);
		} catch {
			setDashboards([]);
		} finally {
			setLoading(false);
		}
	}, [token, user?.id]);

	useEffect(() => {
		fetchDashboards();
	}, [fetchDashboards]);

	const authorizedDashboards = useMemo(
		() =>
			dashboards
				.map((dashboard) => {
					if (dashboard?.subhue === true) {
						return hasFullUnitAccess ? dashboard : null;
					}

					const dashboardUnits = Array.isArray(dashboard?.unidades)
						? dashboard.unidades
						: [];
					const allowedUnits = hasFullUnitAccess
						? dashboardUnits
						: dashboardUnits.filter((unit) =>
								isAllowedUnit(unit, accessibleProfileUnits),
							);

					return allowedUnits.length
						? { ...dashboard, unidades: allowedUnits }
						: null;
				})
				.filter(Boolean),
		[dashboards, hasFullUnitAccess, accessibleProfileUnits],
	);

	const panels = useMemo(
		() => groupPanelsByUnit(authorizedDashboards),
		[authorizedDashboards],
	);

	const units = useMemo(() => {
		const unique = new Map();
		authorizedDashboards.forEach((dashboard) => {
			(dashboard?.unidades || []).forEach((unit) => {
				const name = getUnitName(unit);
				if (name) unique.set(normalize(name), { ...unit, name });
			});
		});
		return Array.from(unique.values()).sort((a, b) =>
			a.name.localeCompare(b.name, 'pt-BR'),
		);
	}, [authorizedDashboards]);

	const unitFilterOptions = useMemo(
		() => [
			{ id: 'all', nome: 'Todas as unidades' },
			...(hasFullUnitAccess ? [{ id: 'subhue', nome: 'SUBHUE' }] : []),
			...units.map((unit) => ({ id: unit.name, nome: unit.name })),
		],
		[hasFullUnitAccess, units],
	);
	const canChooseUnit = units.length > 1;
	const singleProfileUnitName = useMemo(() => {
		if (hasFullUnitAccess) return '';
		if (units.length === 1) return units[0]?.name || '';
		if (accessibleProfileUnits.length === 1) {
			return getUnitName(accessibleProfileUnits[0]);
		}
		return '';
	}, [accessibleProfileUnits, hasFullUnitAccess, units]);
	const effectiveSelectedUnit =
		selectedUnit === 'all' && singleProfileUnitName
			? singleProfileUnitName
			: selectedUnit;
	const panelsForSelectedUnit = useMemo(() => {
		if (effectiveSelectedUnit === 'all') return panels;
		if (effectiveSelectedUnit === 'subhue') {
			return panels
				.filter((panel) => Boolean(panel?.centralDashboard))
				.map((panel) => ({
					...panel,
					unidades: [],
					sourceDashboard: panel.centralDashboard,
				}));
		}
		return panels.reduce((filteredPanels, panel) => {
			const selectedUnits = (panel?.unidades || []).filter(
				(unit) => getUnitName(unit) === effectiveSelectedUnit,
			);
			if (!selectedUnits.length) return filteredPanels;

			filteredPanels.push({
				...panel,
				centralDashboard: null,
				unidades: selectedUnits,
			});
			return filteredPanels;
		}, []);
	}, [effectiveSelectedUnit, panels]);

	const categories = useMemo(() => {
		const names = Array.from(
			new Set(
				panelsForSelectedUnit
					.map((item) => item?.categoria_nome)
					.filter(Boolean),
			),
		).sort((a, b) => a.localeCompare(b, 'pt-BR'));
		return names.map((name, index) => ({
			id: name,
			name,
			color: CATEGORY_COLORS[index % CATEGORY_COLORS.length],
		}));
	}, [panelsForSelectedUnit]);

	useEffect(() => {
		if (
			selectedCategory !== 'all' &&
			!categories.some((category) => category.id === selectedCategory)
		) {
			setSelectedCategory('all');
		}
	}, [categories, selectedCategory]);

	const categoryMap = useMemo(
		() => new Map(categories.map((category) => [category.id, category])),
		[categories],
	);

	const visiblePanels = useMemo(() => {
		const term = normalize(search);
		return panelsForSelectedUnit.filter((panel) => {
			if (
				selectedCategory !== 'all' &&
				panel?.categoria_nome !== selectedCategory
			)
				return false;
			if (term && !normalize(panel?.nome).includes(term)) return false;
			return true;
		});
	}, [panelsForSelectedUnit, search, selectedCategory]);

	const groupedPanels = useMemo(
		() =>
			categories
				.map((category) => ({
					category,
					panels: visiblePanels.filter(
						(panel) => panel?.categoria_nome === category.id,
					),
				}))
				.filter((group) => group.panels.length > 0),
		[categories, visiblePanels],
	);

	const categoryCount = useCallback(
		(categoryId) =>
			panelsForSelectedUnit.filter(
				(panel) => panel?.categoria_nome === categoryId,
			).length,
		[panelsForSelectedUnit],
	);

	const drawerUnits = useMemo(() => {
		const term = normalize(drawerSearch);
		return (drawerDashboard?.unidades || []).filter((unit) =>
			normalize(getUnitName(unit)).includes(term),
		);
	}, [drawerDashboard, drawerSearch]);
	const drawerFamilies = useMemo(() => {
		const families = new Map();
		drawerUnits.forEach((unit) => {
			const family = getUnitFamily(getUnitName(unit));
			if (!families.has(family.id))
				families.set(family.id, { ...family, units: [] });
			families.get(family.id).units.push(unit);
		});
		return Array.from(families.values()).sort(
			(a, b) =>
				UNIT_FAMILY_ORDER.indexOf(a.id) -
				UNIT_FAMILY_ORDER.indexOf(b.id),
		);
	}, [drawerUnits]);

	const closeDrawer = useCallback(() => setDrawerDashboard(null), []);
	const openDashboard = useCallback((dashboard) => {
		if (!dashboard?.link) return;
		setSelectedDashboard(dashboard);
		setDrawerDashboard(null);
	}, []);
	const openPanel = useCallback(
		(panel) => {
			const units = panel?.unidades || [];
			const destinationsCount =
				units.length + Number(Boolean(panel?.centralDashboard));
			if (destinationsCount === 1) {
				openDashboard(
					panel?.centralDashboard ||
						units[0]?.dashboard ||
						panel?.sourceDashboard,
				);
				return;
			}
			setDrawerSearch('');
			setDrawerDashboard(panel);
			requestAnimationFrame(() => drawerSearchRef.current?.focus());
		},
		[openDashboard],
	);

	useEffect(() => {
		if (!drawerDashboard) return undefined;
		const closeOnEscape = (event) =>
			event.key === 'Escape' && closeDrawer();
		document.body.style.overflow = 'hidden';
		document.addEventListener('keydown', closeOnEscape);
		return () => {
			document.body.style.overflow = '';
			document.removeEventListener('keydown', closeOnEscape);
		};
	}, [closeDrawer, drawerDashboard]);

	if (!user) {
		return (
			<div className='flex min-h-screen flex-col'>
				<NavBar />
				<section className='mx-auto my-auto mt-[150px] w-[90%] rounded-md bg-[#004A80] sm:w-[70%] md:w-[60%] lg:w-[50%]'>
					<div className='mx-auto max-w-screen-xl px-4 py-8 text-center lg:px-6 lg:py-16'>
						<h1 className='mb-4 text-5xl font-extrabold text-white sm:text-6xl'>
							401
						</h1>
						<p className='mb-7 text-2xl font-bold text-slate-100'>
							Acesso negado
						</p>
						<p className='text-sm font-light text-gray-300 sm:text-base'>
							Você não possui permissão para acessar esta página.
						</p>
					</div>
				</section>
			</div>
		);
	}

	if (selectedDashboard) {
		return (
			<div className='flex min-h-screen flex-col'>
				<NavBar contentMaxWidthClassName='max-w-[1136px]' />
				<main className='pm-frame-page'>
					<div className='pm-frame-container'>
						<button
							type='button'
							onClick={() => setSelectedDashboard(null)}
							className='pm-back-button'>
							<MdArrowBack size={18} /> Voltar aos painéis
						</button>
						<iframe
							src={selectedDashboard.link}
							title={selectedDashboard.nome}
							className='pm-dashboard-frame'
							allowFullScreen
						/>
					</div>
				</main>
			</div>
		);
	}

	return (
		<div className='pm-root flex min-h-screen flex-col'>
			<NavBar contentMaxWidthClassName='max-w-[1136px]' />
			<section className='pm-filter-band'>
				<div className='pm-filter-inner'>
					<div className='pm-filter-title'>
						<h1>Painéis de monitoramento</h1>
						<p>
							{hasFullUnitAccess
								? user?.smsrio === true ||
									normalize(user?.cargo) === 'sms'
									? 'SMS'
									: 'SUBHUE'
								: singleProfileUnitName || 'UNIDADE'}
						</p>
					</div>
					<div className='pm-filters'>
						<label className='pm-input-search'>
							<MdSearch size={20} />
							<input
								type='search'
								placeholder='Nome do painel'
								value={search}
								onChange={(event) =>
									setSearch(event.target.value)
								}
							/>
						</label>
						{canChooseUnit && (
							<div className='pm-unit-filter'>
								<SearchableSetorSelect
									value={selectedUnit}
									options={unitFilterOptions}
									onChange={setSelectedUnit}
									placeholder='Todas as unidades'
									ariaLabel='Filtrar por unidade'
									emptyMessage='Nenhuma unidade encontrada'
									clearDisplayWhenValue='all'
									showAllOptionsOnOpen
									className='h-[42px] rounded-[8px] border border-[#E3E3E3] bg-white px-3 text-[14px] text-[#16233c] outline-none'
								/>
							</div>
						)}
					</div>
				</div>
			</section>

			<main className='pm-content flex-1'>
				<div className='pm-container'>
					<aside className='pm-cat-card'>
						<div className='pm-cat-mobile-select'>
							<MdApps size={20} />
							<select
								aria-label='Filtrar por categoria'
								value={selectedCategory}
								onChange={(event) =>
									setSelectedCategory(event.target.value)
								}>
								<option value='all'>Todas as categorias</option>
								{categories.map((category) => (
									<option
										key={category.id}
										value={category.id}>
										{category.name} (
										{categoryCount(category.id)})
									</option>
								))}
							</select>
						</div>
						<div className='pm-cat-list'>
							<button
								type='button'
								className={`pm-cat-item pm-cat-item--all ${selectedCategory === 'all' ? 'is-active' : ''}`}
								onClick={() => setSelectedCategory('all')}>
								<MdApps size={19} />
								<span className='pm-cat-name'>
									Todas as categorias
								</span>
								<span className='pm-cat-count'>
									{categories.length}
								</span>
							</button>
							{categories.map((category) => (
								<button
									key={category.id}
									type='button'
									className={`pm-cat-item ${selectedCategory === category.id ? 'is-active' : ''}`}
									onClick={() =>
										setSelectedCategory(category.id)
									}>
									<span
										className='pm-cat-dot'
										style={{ background: category.color }}
									/>
									<span className='pm-cat-name'>
										{category.name}
									</span>
									<span className='pm-cat-count'>
										{categoryCount(category.id)}
									</span>
								</button>
							))}
						</div>
					</aside>

					<section className='pm-content-col'>
						<div className='pm-list-header'>
							<div>
								<h2>
									{selectedCategory === 'all'
										? 'Todos os painéis'
										: categoryMap.get(selectedCategory)
												?.name || 'Painéis'}
								</h2>
								<p className='pm-list-sub'>
									{String(visiblePanels.length).padStart(
										2,
										'0',
									)}{' '}
									painé
									{visiblePanels.length === 1 ? 'l' : 'is'}
								</p>
							</div>
						</div>
						{loading ? (
							<div className='pm-empty-state'>
								Carregando painéis...
							</div>
						) : groupedPanels.length === 0 ? (
							<div className='pm-empty-state'>
								<MdSearch size={36} />
								Nenhum painel encontrado com esses filtros.
							</div>
						) : (
							<div className='pm-groups'>
								{groupedPanels.map(
									({ category, panels: categoryPanels }) => (
										<section
											className='pm-cat-group'
											key={category.id}>
											{selectedCategory === 'all' && (
												<div className='pm-group-head'>
													<h3>{category.name}</h3>
													<span className='pm-group-meta'>
														{categoryPanels.length}{' '}
														painé
														{categoryPanels.length ===
														1
															? 'l'
															: 'is'}
													</span>
												</div>
											)}
											<div className='pm-card-grid'>
												{categoryPanels.map(
													(panel, index) => {
														const unitsCount = (
															panel?.unidades ||
															[]
														).length;
														const unitLabel =
															effectiveSelectedUnit !==
																'all' &&
															effectiveSelectedUnit !==
																'subhue'
																? effectiveSelectedUnit
																: unitsCount
																	? `${unitsCount} unidade${unitsCount === 1 ? '' : 's'}`
																	: 'SUBHUE';
														return (
															<button
																key={
																	panel?.id ||
																	`${category.id}-${index}`
																}
																type='button'
																className='pm-panel-card'
																style={{
																	'--cat-color':
																		category.color,
																}}
																onClick={() =>
																	openPanel(
																		panel,
																	)
																}>
																<div className='pm-panel-thumb'>
																	{panel?.icone ? (
																		<img
																			src={
																				panel.icone
																			}
																			alt=''
																		/>
																	) : panel?.centralDashboard ? (
																		<MdInsights
																			size={
																				24
																			}
																		/>
																	) : (
																		<MdApartment
																			size={
																				24
																			}
																		/>
																	)}
																</div>
																<div className='pm-panel-body'>
																	<span className='pm-panel-bar' />
																	<div className='pm-panel-meta'>
																		<span className='pm-panel-title'>
																			{
																				panel?.nome
																			}
																		</span>
																		<span className='pm-chip'>
																			{unitsCount ? (
																				<MdApartment
																					size={
																						14
																					}
																				/>
																			) : (
																				<MdPublic
																					size={
																						14
																					}
																				/>
																			)}
																			{
																				unitLabel
																			}
																		</span>
																	</div>
																</div>
															</button>
														);
													},
												)}
											</div>
										</section>
									),
								)}
							</div>
						)}
					</section>
				</div>
			</main>

			<div
				className={`pm-scrim ${drawerDashboard ? 'is-open' : ''}`}
				onClick={closeDrawer}
			/>
			<aside
				className={`pm-drawer ${drawerDashboard ? 'is-open' : ''}`}
				role='dialog'
				aria-modal='true'
				aria-label='Selecionar unidade do painel'>
				{drawerDashboard && (
					<>
						<div className='pm-drawer-head'>
							<div className='pm-drawer-title-row'>
								<span
									className='pm-drawer-title-bar'
									style={{
										background:
											categoryMap.get(
												drawerDashboard?.categoria_nome,
											)?.color || CATEGORY_COLORS[0],
									}}
								/>
								<div className='pm-drawer-title-text'>
									<p>{drawerDashboard?.categoria_nome}</p>
									<h3>{drawerDashboard?.nome}</h3>
								</div>
								<button
									type='button'
									onClick={closeDrawer}
									aria-label='Fechar'>
									<MdClose size={22} />
								</button>
							</div>
							<p className='pm-drawer-description'>
								Escolha abaixo a unidade ou o nível central para
								abrir este painel.
							</p>
						</div>
						<div className='pm-drawer-search-block'>
							<div className='pm-drawer-search-label'>
								<span>Abrir por unidade</span>
								<span>
									{drawerUnits.length} de{' '}
									{(drawerDashboard?.unidades || []).length}
								</span>
							</div>
							<label className='pm-drawer-input'>
								<MdSearch size={18} />
								<input
									ref={drawerSearchRef}
									type='search'
									placeholder='Unidade'
									value={drawerSearch}
									onChange={(event) =>
										setDrawerSearch(event.target.value)
									}
								/>
							</label>
						</div>
						<div className='pm-drawer-list'>
							{drawerDashboard?.centralDashboard && (
								<section className='pm-unit-family pm-central-family'>
									<div className='pm-unit-family-head'>
										<span>Nível central</span>
										<i />
									</div>
									<button
										type='button'
										className='pm-unit-row'
										onClick={() =>
											openDashboard(
												drawerDashboard.centralDashboard,
											)
										}>
										<span className='pm-unit-dot pm-unit-dot--central' />
										<span>SUBHUE</span>
										<MdArrowOutward size={17} />
									</button>
								</section>
							)}
							{drawerFamilies.length ? (
								drawerFamilies.map((family) => (
									<section
										className='pm-unit-family'
										key={family.id}>
										<div className='pm-unit-family-head'>
											<span>{family.name}</span>
											<i />
										</div>
										{family.units.map((unit, index) => (
											<button
												key={
													unit?.id ||
													`${getUnitName(unit)}-${index}`
												}
												type='button'
												className='pm-unit-row'
												onClick={() =>
													openDashboard(
														unit?.dashboard,
													)
												}>
												<span
													className='pm-unit-dot'
													style={{
														background:
															family.color,
													}}
												/>
												<span>{getUnitName(unit)}</span>
												<MdArrowOutward size={17} />
											</button>
										))}
									</section>
								))
							) : (
								<div className='pm-drawer-empty'>
									Nenhuma unidade encontrada.
								</div>
							)}
						</div>
					</>
				)}
			</aside>
		</div>
	);
};

export default PaineisMonitoramento;
